define("shared/utils/media-adjustments/request-cache", ["exports", "shared/utils/media-adjustments/normalize"], function (_exports, _normalize) {
  "use strict";

  Object.defineProperty(_exports, "__esModule", {
    value: true
  });
  _exports.buildMediaItemAdjustmentsCacheKey = buildMediaItemAdjustmentsCacheKey;
  _exports.buildPostMediaItemAdjustmentsCacheKey = buildPostMediaItemAdjustmentsCacheKey;
  _exports.clearMediaAdjustmentsRequestCache = clearMediaAdjustmentsRequestCache;
  _exports.fetchWithCache = fetchWithCache;
  _exports.invalidateMediaAdjustmentsCacheKey = invalidateMediaAdjustmentsCacheKey;
  _exports.normalizedParamEntries = normalizedParamEntries;
  _exports.stableParamsKey = stableParamsKey;
  0; //eaimeta@70e063a35619d71f0,"shared/utils/media-adjustments/normalize"eaimeta@70e063a35619d71f
  /**
   * Module-level response cache for media-adjustments API calls. Cleared on logout via
   * clearMediaAdjustmentsRequestCache(). LRU-bounded so long-lived sessions do not grow without limit.
   */
  const MAX_CACHE_ENTRIES = 100;
  const TRANSIENT_PREVIEW_STATUSES = new Set(['processing', 'not_requested']);
  const responseCache = new Map();
  const inFlightRequests = new Map();
  function setCachedResponse(cacheKey, response) {
    if (responseCache.has(cacheKey)) {
      responseCache.delete(cacheKey);
    }
    responseCache.set(cacheKey, response);
    if (responseCache.size > MAX_CACHE_ENTRIES) {
      const oldestKey = responseCache.keys().next().value;
      if (oldestKey !== undefined) {
        responseCache.delete(oldestKey);
      }
    }
  }

  /**
   * Canonical, null-stripped `[key, stringValue]` pairs for a request-params object. The single place
   * that knows how each param serializes (notably that `crop_array` is JSON-encoded). Shared by the
   * cache keys, the query string, and the stale-spec signature so the three cannot drift apart.
   */
  function normalizedParamEntries(params) {
    return Object.entries(params).filter(_ref => {
      let [, value] = _ref;
      return value !== undefined && value !== null;
    }).map(_ref2 => {
      let [key, value] = _ref2;
      if (key === 'crop_array' && Array.isArray(value)) {
        return [key, JSON.stringify(value)];
      }
      return [key, String(value)];
    });
  }
  function stableParamsKey(params) {
    return normalizedParamEntries(params).sort((_ref3, _ref4) => {
      let [leftKey] = _ref3;
      let [rightKey] = _ref4;
      return leftKey.localeCompare(rightKey);
    }).map(_ref5 => {
      let [key, value] = _ref5;
      return `${key}=${value}`;
    }).join('&');
  }
  function buildMediaItemAdjustmentsCacheKey(mediaItemId, params) {
    return `media-item:${mediaItemId}:${stableParamsKey(params)}`;
  }
  function buildPostMediaItemAdjustmentsCacheKey(postMediaItemId, params) {
    const base = `post-media-item:${postMediaItemId}`;
    if (!params) {
      return base;
    }
    const paramsKey = stableParamsKey(params);
    return paramsKey ? `${base}:${paramsKey}` : base;
  }
  function isCacheableResponse(response) {
    const adjustments = response.media_adjustments;
    if (!adjustments || Object.keys(adjustments).length === 0) {
      return false;
    }
    return !Object.values(adjustments).some(adjustment => TRANSIENT_PREVIEW_STATUSES.has((0, _normalize.previewStatusOf)(adjustment) ?? 'not_requested'));
  }
  function clearMediaAdjustmentsRequestCache() {
    responseCache.clear();
    inFlightRequests.clear();
  }
  function invalidateMediaAdjustmentsCacheKey(cacheKey) {
    responseCache.delete(cacheKey);
  }
  async function fetchWithCache(cacheKey, fetchFn, options) {
    if (options?.bypassCache) {
      return fetchFn();
    }
    const cachedResponse = responseCache.get(cacheKey);
    if (cachedResponse) {
      return cachedResponse;
    }
    const inFlightRequest = inFlightRequests.get(cacheKey);
    if (inFlightRequest) {
      return inFlightRequest;
    }
    const request = fetchFn().then(response => {
      inFlightRequests.delete(cacheKey);
      if (isCacheableResponse(response)) {
        setCachedResponse(cacheKey, response);
      }
      return response;
    }, error => {
      inFlightRequests.delete(cacheKey);
      throw error;
    });
    inFlightRequests.set(cacheKey, request);
    return request;
  }
});